import { useState } from "react"

import cn from "classnames"
import { add } from "date-fns"
import { useAtom } from "jotai"
import { useSWRConfig } from "swr"

import { IconButton } from "components/Button"
import { toastAtom } from "components/dashboard/Toast"
import FaIcon from "components/FontAwesomeIcon"
import { formatDateForDisplay } from "utils/dates"
import { isStatusCodeSuccess } from "utils/helpers"

import type { OptionsForm } from "../OptionsForm"
import type { Availability } from "hooks/useAvailability"
import type {
  CreateBusyTimeApiRequest,
  CreateBusyTimeApiResponse
} from "pages/api/busy-time/create"
import type { DeleteBusyTimeApiResponse } from "pages/api/busy-time/delete"

const AdminTime: React.FC<{
  availability: Availability
  bookingInterval: OptionsForm["bookingInterval"] | undefined
  userId?: string
}> = ({ availability, bookingInterval, userId }) => {
  const [actionsOpen, setActionsOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [, setToast] = useAtom(toastAtom)
  const { mutate } = useSWRConfig()
  const formattedDate = formatDateForDisplay(availability.dateTime, null)

  const hasSlots = availability.slots > 0
  // each user gets 1 slot per time but the busyTimes api returns it in an array. If the length is 1, then the user has marked themselves as busy already
  const isBusy = availability?.busyTimes?.length === 1

  const buttonClass = cn("!p-2 relative", {
    "bg-error text-white hover:!bg-error-dark": isBusy,
    "bg-warning text-white hover:!bg-warning-dark":
      availability.bookings && availability?.bookings.length > 0,
    "bg-gradient-to-br from-[#f43f5e_40%] to-[#f59e0b_60%] hover:from-[#e11d48_40%] hover:to-[#d97706_60%]":
      isBusy && availability.bookings && availability?.bookings.length > 0,
    "bg-white hover:bg-gray-200": hasSlots
  })

  return (
    <li className="cursor-pointer border-2 border-gray-200 text-center !select-none">
      <div
        className={buttonClass}
        onClick={() => setActionsOpen(prev => !prev)}
      >
        {formattedDate}
        {actionsOpen && (
          <button
            className="absolute top-1/2 -translate-y-1/2 right-3"
            type="button"
          >
            <FaIcon icon="times" className="w-5 h-5 cursor-pointer" />
          </button>
        )}
      </div>
      {actionsOpen && (
        <div className="bg-gray-100 p-3 flex flex-col gap-3 items-center justify-center text-sm">
          {availability.bookings && availability?.bookings.length > 0 && (
            <div>
              <p className="font-bold my-0">Bookings</p>
              <p className="my-0">
                {availability.bookings.map(b => b.id).join(", ")}
              </p>
            </div>
          )}
          {availability.busyTimes && availability?.busyTimes.length > 0 && (
            <div>
              <p className="font-bold my-0">Busy staff</p>
              <p className="my-0">
                {availability.busyTimes.map(b => b.user?.email).join(", ")}
              </p>
            </div>
          )}
          {/* Dont allow people to set themselves as busy when all the slots are full - Allow them to mark themselves as available though */}
          {(hasSlots || isBusy) && (
            <IconButton
              icon={isBusy ? "check" : "times"}
              saving={isDeleting}
              onClick={async () => {
                setIsDeleting(true)
                const confirm = window.confirm(
                  `Are you sure you want to set ${formattedDate} as ${
                    isBusy ? "available" : "busy"
                  }?`
                )
                if (confirm && userId) {
                  await fetch(
                    `/api/busy-time/${
                      isBusy
                        ? `delete?id=${availability?.busyTimes?.[0].id}&userId=${userId}`
                        : "create"
                    }`,
                    {
                      method: isBusy ? "DELETE" : "POST",
                      ...(!isBusy && {
                        body: JSON.stringify({
                          startTime: availability.dateTime,
                          endTime: add(availability.dateTime, {
                            minutes: bookingInterval || 60
                          }),
                          userId
                        } as CreateBusyTimeApiRequest["body"])
                      })
                    }
                  )
                    .then(r => r.json())
                    .then(
                      (
                        res:
                          | CreateBusyTimeApiResponse
                          | DeleteBusyTimeApiResponse
                      ) => {
                        setIsDeleting(false)
                        setActionsOpen(false)
                        setToast({
                          variant: isStatusCodeSuccess(res.statusCode)
                            ? "success"
                            : "error",
                          content: res.message
                        })

                        // mutate where the busy times are used
                        mutate(
                          key =>
                            typeof key === "string" &&
                            key.startsWith("/api/busy-time/read")
                        )
                      }
                    )
                    .catch(err => console.error(err))
                } else {
                  setIsDeleting(false)
                }
              }}
            >
              Set as {isBusy ? "available" : "busy"}
            </IconButton>
          )}
        </div>
      )}
    </li>
  )
}

export default AdminTime
